import React from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  BookOpen,
  Printer,
  Package,
  Download
} from 'lucide-react';

const BookModeOverlay = ({
  result,
  onClose,
  onSelectMatch,
}) => {
  if (!result) return null;

  // Multiple matches - show selection UI
  if (result.multipleMatches) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-96 bg-white rounded-lg shadow-2xl overflow-hidden">
          <div className="p-4 bg-amber-500 text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            <span className="font-semibold">Multiple Matches Found</span>
          </div>
          <div className="p-4">
            <p className="text-sm text-slate-600 mb-3">
              Barcode <code className="bg-slate-100 px-1 rounded">{result.scannedCode}</code> matches {result.matches.length} items:
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {result.matches.map((match, index) => (
                <button
                  key={match.id || index}
                  onClick={() => onSelectMatch(match)}
                  className="w-full flex items-center gap-3 p-2 border rounded hover:bg-slate-50 transition-colors text-left"
                >
                  <img
                    src={match.item?.images?.[0]?.large_url || 'https://via.placeholder.com/40'}
                    alt=""
                    className="w-10 h-10 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{match.item?.title}</p>
                    <p className="text-xs text-slate-500">{match.item?.merchant_sku}</p>
                  </div>
                </button>
              ))}
            </div>
            <button
              className="w-full mt-3 px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  if (result.success === null) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-80 bg-white rounded-lg shadow-2xl text-center p-6">
          <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin mb-4" />
          <p className="text-lg font-medium">Processing...</p>
          {result.item && (
            <p className="text-sm text-slate-500 mt-2 truncate">{result.item.title}</p>
          )}
        </div>
      </div>
    );
  }

  // Helper to highlight matching portion
  const highlightMatch = (scanned, matched) => {
    if (!scanned || !matched) return scanned;
    const normalizedScanned = scanned.replace(/-/g, '');
    const normalizedMatched = matched.replace(/-/g, '');

    // Check if exact match
    if (normalizedScanned === normalizedMatched) {
      return <span className="text-green-600 font-semibold">{scanned}</span>;
    }

    // Check if matched is substring (partial match)
    if (normalizedScanned.includes(normalizedMatched)) {
      const idx = normalizedScanned.indexOf(normalizedMatched);
      return (
        <>
          <span className="text-slate-400">{scanned.slice(0, idx)}</span>
          <span className="text-green-600 font-semibold">{scanned.slice(idx, idx + normalizedMatched.length)}</span>
          <span className="text-slate-400">{scanned.slice(idx + normalizedMatched.length)}</span>
        </>
      );
    }

    // Check if scanned is substring of matched (leading/trailing dropped)
    if (normalizedMatched.includes(normalizedScanned)) {
      return <span className="text-green-600 font-semibold">{scanned}</span>;
    }

    // Partial match - highlight what overlaps
    const withoutLeading = normalizedScanned.substring(1);
    const withoutTrailing = normalizedScanned.substring(0, normalizedScanned.length - 1);

    if (normalizedMatched === withoutLeading) {
      return (
        <>
          <span className="text-slate-400 line-through">{scanned[0]}</span>
          <span className="text-green-600 font-semibold">{scanned.slice(1)}</span>
        </>
      );
    }

    if (normalizedMatched === withoutTrailing) {
      return (
        <>
          <span className="text-green-600 font-semibold">{scanned.slice(0, -1)}</span>
          <span className="text-slate-400 line-through">{scanned.slice(-1)}</span>
        </>
      );
    }

    return scanned;
  };

  // Success state
  if (result.success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-[420px] bg-white rounded-lg shadow-2xl overflow-hidden">
          {/* Success header */}
          <div className="bg-green-500 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">{result.message}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {result.labelSaved ? (
                  <Download className="w-4 h-4" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                <CheckCircle2 className="w-3 h-3" />
              </div>
              <div className="flex items-center gap-1">
                <Package className="w-4 h-4" />
                <CheckCircle2 className="w-3 h-3" />
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* Product info with thumbnail */}
            {result.item && (
              <div className="flex gap-3 mb-4">
                <img
                  src={result.item.images?.[0]?.large_url || result.item.images?.[0]?.url || 'https://via.placeholder.com/80'}
                  alt=""
                  className="w-20 h-20 object-cover rounded border"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-2">{result.item.title}</p>
                  <div className="mt-1 space-y-0.5">
                    <p className="text-xs text-slate-500">
                      <span className="text-slate-400">SKU:</span> {result.item.merchant_sku}
                    </p>
                    {result.item.asin && (
                      <p className="text-xs text-slate-500">
                        <span className="text-slate-400">ASIN:</span> {result.item.asin}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Scanned code and match info */}
            {result.scannedCode && (
              <div className="p-3 bg-slate-50 rounded-lg mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500 uppercase">Scanned</span>
                  {result.matchType && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      {result.matchType}
                    </span>
                  )}
                </div>
                <p className="font-mono text-sm">
                  {highlightMatch(result.scannedCode, result.matchedIdentifier)}
                </p>
                {result.matchedIdentifier && result.scannedCode !== result.matchedIdentifier && (
                  <p className="text-xs text-slate-500 mt-1">
                    Matched: <span className="font-mono">{result.matchedIdentifier}</span>
                  </p>
                )}
              </div>
            )}

            {/* FNSKU label status */}
            {result.item?.fnsku && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  {result.labelSaved ? (
                    <Download className="w-4 h-4 text-blue-500" />
                  ) : (
                    <Printer className="w-4 h-4 text-blue-500" />
                  )}
                  <span className="text-xs text-blue-600 uppercase font-medium">
                    FNSKU Label {result.labelSaved ? 'Saved' : 'Printed'}
                  </span>
                </div>
                <p className="font-mono text-lg font-bold text-blue-700">{result.item.fnsku}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-80 bg-white rounded-lg shadow-2xl text-center p-6">
        <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
        <p className="text-lg font-medium text-red-700">{result.message}</p>
        {result.item && (
          <div className="mt-3 text-sm text-slate-600">
            <p className="font-medium truncate">{result.item.title}</p>
          </div>
        )}
        <button
          className="mt-4 px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50 transition-colors"
          onClick={onClose}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default BookModeOverlay;
